/* Meltiva Header v4.1 — contained interaction + cart bridge */
(() => {
  const SELECTOR = '[data-meltiva-header]';
  const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');

  const getFocusable = (container) =>
    Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.offsetParent !== null);

  const animateCount = (element) => {
    element.classList.remove('is-ticking');
    void element.offsetWidth;
    element.classList.add('is-ticking');

    window.setTimeout(() => {
      element.classList.remove('is-ticking');
    }, 180);
  };

  const updateCartCount = (root, count) => {
    const normalizedCount = Number.isFinite(Number(count))
      ? Math.max(0, Number(count))
      : 0;

    root.querySelectorAll('[data-mvh-cart-count]').forEach((element) => {
      const nextValue = String(normalizedCount);
      const changed = element.textContent.trim() !== nextValue;

      element.textContent = nextValue;

      if (changed) {
        animateCount(element);
      }
    });

    root.querySelectorAll('[data-mvh-cart]').forEach((link) => {
      link.setAttribute(
        'aria-label',
        `Open cart, ${normalizedCount} ${normalizedCount === 1 ? 'item' : 'items'}`
      );
    });
  };

  const fetchCart = async (root) => {
    try {
      const base = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${base}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) return;

      const cart = await response.json();
      updateCartCount(root, cart.item_count || 0);
    } catch (error) {
      // Keep the server-rendered count when cart refresh fails.
    }
  };

  const parseCount = (value) => {
    const parsed = Number.parseInt(
      String(value ?? '').replace(/[^\d]/g, ''),
      10
    );

    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  };

  const countFromCartBridge = () => {
    const bridge = document.getElementById('cart-icon-bubble');
    if (!bridge) return null;

    const visible = bridge.querySelector(
      '.cart-count-bubble [aria-hidden="true"]'
    );
    const visibleCount = parseCount(visible?.textContent);

    if (visibleCount !== null) return visibleCount;

    const hidden = bridge.querySelector(
      '.cart-count-bubble .visually-hidden'
    );
    const hiddenCount = parseCount(hidden?.textContent);

    if (hiddenCount !== null) return hiddenCount;

    return bridge.querySelector('.cart-count-bubble') ? null : 0;
  };

  const syncFromCartBridge = (root) => {
    const count = countFromCartBridge();

    if (count === null) {
      fetchCart(root);
      return;
    }

    updateCartCount(root, count);
  };

  const cartCountFromEvent = (event) => {
    const candidates = [
      event?.detail?.cart?.item_count,
      event?.detail?.item_count,
      event?.detail?.cartItemCount,
      event?.detail?.count
    ];

    const value = candidates.find((candidate) =>
      Number.isFinite(Number(candidate))
    );

    return value === undefined ? null : Number(value);
  };

  const tryOpenSearch = (root) => {
    const detailsModal = Array.from(
      document.querySelectorAll(
        'details-modal.header__search, details-modal[id*="Search"], [data-search-modal]'
      )
    ).find((element) => !root.contains(element));

    if (detailsModal) {
      const details = detailsModal.querySelector('details');

      if (details) {
        details.open = true;
        details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      } else {
        detailsModal.removeAttribute('hidden');
        detailsModal.setAttribute('aria-hidden', 'false');
        detailsModal.classList.add('is-open', 'active', 'open');
      }

      window.setTimeout(() => {
        detailsModal.querySelector('input[type="search"]')?.focus();
      }, 60);

      return true;
    }

    const existingTrigger = Array.from(
      document.querySelectorAll(
        '.header__icon--search, [data-search-open], [aria-controls*="Search"]'
      )
    ).find((element) => !root.contains(element));

    if (existingTrigger) {
      existingTrigger.click();
      return true;
    }

    return false;
  };

  const tryOpenCartDrawer = (root, trigger) => {
    const customDrawer = document.querySelector('cart-drawer');

    if (customDrawer && typeof customDrawer.open === 'function') {
      customDrawer.open(trigger);
      return true;
    }

    const detailsDrawer = document.querySelector(
      'details[id*="CartDrawer"], details[data-cart-drawer]'
    );

    if (detailsDrawer) {
      detailsDrawer.open = true;
      return true;
    }

    const existingTrigger = Array.from(
      document.querySelectorAll(
        '[data-cart-drawer-open], [aria-controls="CartDrawer"], .header__icon--cart, .cart-drawer__trigger'
      )
    ).find((element) => !root.contains(element));

    if (existingTrigger) {
      existingTrigger.click();
      return true;
    }

    const genericDrawer = document.querySelector(
      '#CartDrawer, [data-cart-drawer], .cart-drawer'
    );

    if (genericDrawer) {
      genericDrawer.removeAttribute('hidden');
      genericDrawer.setAttribute('aria-hidden', 'false');
      genericDrawer.classList.add('is-open', 'active', 'open');
      document.body.classList.add('overflow-hidden');
      return true;
    }

    return false;
  };

  const initHeader = (root) => {
    if (!root || root.dataset.mvhReady === 'true') return;
    root.dataset.mvhReady = 'true';

    const shop = root.querySelector('[data-mvh-shop]');
    const shopTrigger = root.querySelector('[data-mvh-shop-trigger]');
    const mega = root.querySelector('[data-mvh-mega]');
    const mobileMenu = root.querySelector('[data-mvh-mobile]');
    const mobileOpen = root.querySelector('[data-mvh-mobile-open]');
    const mobileClose = root.querySelector('[data-mvh-mobile-close]');
    const searchTrigger = root.querySelector('[data-mvh-search]');
    const cartTriggers = Array.from(root.querySelectorAll('[data-mvh-cart]'));

    let shopCloseTimer = null;
    let lastMobileFocus = null;

    const setInert = (element, value) => {
      if (element && 'inert' in element) {
        element.inert = value;
      }
    };

    const openMega = () => {
      if (!DESKTOP_QUERY.matches || !shop || !shopTrigger || !mega) return;

      window.clearTimeout(shopCloseTimer);
      shop.classList.add('is-open');
      shopTrigger.setAttribute('aria-expanded', 'true');
      mega.setAttribute('aria-hidden', 'false');
      setInert(mega, false);
    };

    const closeMega = (returnFocus = false) => {
      if (!shop || !shopTrigger || !mega) return;

      window.clearTimeout(shopCloseTimer);
      shop.classList.remove('is-open');
      shopTrigger.setAttribute('aria-expanded', 'false');
      mega.setAttribute('aria-hidden', 'true');
      setInert(mega, true);

      if (returnFocus) {
        shopTrigger.focus();
      }
    };

    const cancelMegaClose = () => {
      window.clearTimeout(shopCloseTimer);
      shopCloseTimer = null;
    };

    const scheduleMegaClose = (delay = 240) => {
      cancelMegaClose();
      shopCloseTimer = window.setTimeout(() => closeMega(false), delay);
    };

    const openMobile = () => {
      if (!mobileMenu || !mobileOpen) return;

      closeMega(false);
      lastMobileFocus = document.activeElement;
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      mobileOpen.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mvh-menu-open');
      setInert(mobileMenu, false);

      window.requestAnimationFrame(() => {
        getFocusable(mobileMenu)[0]?.focus();
      });
    };

    const closeMobile = (returnFocus = true) => {
      if (!mobileMenu || !mobileOpen) return;

      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      mobileOpen.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mvh-menu-open');
      setInert(mobileMenu, true);

      if (returnFocus) {
        (lastMobileFocus || mobileOpen).focus();
      }
    };

    setInert(mega, true);
    setInert(mobileMenu, true);

    shopTrigger?.addEventListener('click', (event) => {
      if (!DESKTOP_QUERY.matches) return;

      event.preventDefault();

      /*
        Pointer clicks are open-only on desktop.
        Hover may already have opened the panel before the click lands;
        treating that click as a toggle would immediately close/inert it.
        Escape, outside click, pointer leave, and scroll still close it.
      */
      openMega();
    });

    shopTrigger?.addEventListener('keydown', (event) => {
      if (!DESKTOP_QUERY.matches) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openMega();
        getFocusable(mega)[0]?.focus();
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openMega();
        getFocusable(mega)[0]?.focus();
      }

      if (event.key === 'Escape') {
        closeMega(true);
      }
    });

    shop?.addEventListener('pointerenter', openMega);
    shop?.addEventListener('pointerleave', () => scheduleMegaClose(320));
    shop?.addEventListener('focusin', openMega);

    shop?.addEventListener('focusout', (event) => {
      if (!shop.contains(event.relatedTarget)) {
        scheduleMegaClose(180);
      }
    });

    mega?.addEventListener('pointerenter', cancelMegaClose);
    mega?.addEventListener('pointerdown', cancelMegaClose);
    mega?.addEventListener('pointerleave', () => scheduleMegaClose(320));

    mega?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMega(true);
      }
    });

    mobileOpen?.addEventListener('click', openMobile);
    mobileClose?.addEventListener('click', () => closeMobile(true));

    mobileMenu?.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) {
        closeMobile(false);
      }
    });

    mobileMenu?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMobile(true);
        return;
      }

      if (event.key !== 'Tab' || !mobileMenu.classList.contains('is-open')) {
        return;
      }

      const focusable = getFocusable(mobileMenu);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    searchTrigger?.addEventListener('click', (event) => {
      closeMega(false);

      if (tryOpenSearch(root)) {
        event.preventDefault();
      }
    });

    cartTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        closeMega(false);
        closeMobile(false);

        if (tryOpenCartDrawer(root, trigger)) {
          event.preventDefault();
        }
      });
    });

    document.addEventListener('click', (event) => {
      if (shop && !shop.contains(event.target)) {
        closeMega(false);
      }
    });

    window.addEventListener(
      'scroll',
      () => {
        closeMega(false);
      },
      { passive: true }
    );

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      closeMega(false);

      if (mobileMenu?.classList.contains('is-open')) {
        closeMobile(true);
      }
    });

    const onBreakpointChange = () => {
      closeMega(false);

      if (DESKTOP_QUERY.matches) {
        closeMobile(false);
      }
    };

    if (typeof DESKTOP_QUERY.addEventListener === 'function') {
      DESKTOP_QUERY.addEventListener('change', onBreakpointChange);
    } else {
      DESKTOP_QUERY.addListener(onBreakpointChange);
    }

    const cartBridge = document.getElementById('cart-icon-bubble');

    if (cartBridge) {
      new MutationObserver(() => {
        syncFromCartBridge(root);
      }).observe(cartBridge, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    [
      'cart:refresh',
      'cart:updated',
      'cart:change',
      'product:added',
      'ajaxProduct:added',
      'theme:cart:change',
      'meltiva:cart:change'
    ].forEach((eventName) => {
      document.addEventListener(eventName, (event) => {
        const eventCount = cartCountFromEvent(event);

        if (eventCount !== null) {
          updateCartCount(root, eventCount);
          return;
        }

        window.requestAnimationFrame(() => {
          syncFromCartBridge(root);
        });
      });
    });

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest('form');
        if (!form) return;

        const action = form.getAttribute('action') || '';

        if (action.includes('/cart/add')) {
          window.setTimeout(() => fetchCart(root), 150);
          window.setTimeout(() => fetchCart(root), 700);
        }
      },
      true
    );

    window.addEventListener('pageshow', () => {
      fetchCart(root);
    });

    syncFromCartBridge(root);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach(initHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
